import { Component, computed, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { uniqueId } from '@co-3/ctrl-ui/common';
import { AuthService } from '@core';
import { InsuranceRank } from '../../../models/insurance-rank';
import { DATE_INVALID_DAY, DATE_INVALID_FORMAT, NAME_TOO_SHORT } from '../../../config/error-messages.const';
import { InsurantsConfig } from '@models/insurants-form-config';
import { InsurenetInterfaceService } from 'app/core/insurenet-interface/insurenet-interface.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-insurant-dynamic-form',
    templateUrl: './insurant-dynamic-form.component.html',
    styleUrls: ['./insurant-dynamic-form.component.scss'],
    standalone: false,
})
export class InsurantDynamicFormComponent implements OnChanges {
    @Input()
    public formGroup: FormGroup;
    @Input()
    public insurantsConfig: InsurantsConfig;
    @Input()
    public insuranceRanks: InsuranceRank[] | undefined;

    // #region errormessages
    public firstNameErrorMessage: string = NAME_TOO_SHORT;
    public lastNameErrorMessage: string = NAME_TOO_SHORT;
    public titleErrorMessage = 'Bitte wähle eine Anrede für die versicherte Person';
    public healthInsuranceErrorMessages = {
        required: 'Bitte gib die gesetzliche Krankenversicherung an',
        minLength: 'Die Krankenversicherung muss mindestens 2 Zeichen lang sein.',
        maxLength: 'Der Name der Krankenversicherung darf max. 70 Zeichen lang sein',
    };

    public missingTeethErrorMessages = {
        required: 'Bitte gib die Anzahl fehlender Zähne an.',
        max: 'Leider ist ein Abschluss bei mehr als drei fehlenden Zähnen nicht möglich.',
    };
    // #endregion

    public gkvNames$: Observable<string[]> = this.insurenetInterfaceService.getGKVList();
    public id = uniqueId();
    public constructor(
        public auth: AuthService,
        private insurenetInterfaceService: InsurenetInterfaceService,
    ) {}

    public get minAge(): number {
        const minAge = this.insurantsConfig?.minAge;
        return minAge ?? 0;
    }

    public get maxAge(): number {
        const maxAge = this.insurantsConfig?.maxAge;
        return maxAge ?? 0;
    }

    public get ranksMandatory(): boolean {
        return !!this.insurantsConfig?.rankIdMandatory;
    }

    public get askForMissingTeeth(): boolean {
        return !!this.insurantsConfig?.rankIdMandatory;
    }

    public get askForTitle(): boolean {
        return this.insurantsConfig?.askForTitle;
    }

    public get askForHealthInsurance(): boolean {
        return this.insurantsConfig?.askForHealthInsurance;
    }

    public get usageNotes(): { [key: string]: string } {
        return this.insurantsConfig?.usageNotes;
    }

    protected get hasSelectedOtherOption(): boolean {
        return (
            this.askForHealthInsurance &&
            typeof this.formGroup.get('healthInsurance').value === 'string' &&
            this.formGroup.get('healthInsurance').value.includes('Sonstige')
        );
    }

    public dateErrorMessages = computed(() => ({
        required: DATE_INVALID_FORMAT,
        ageTooLow: this.formGroup?.get('isInsuree')?.value
            ? `Du musst mindestens ${this.minAge} Jahre alt sein, um eine Versicherung abschließen zu können.`
            : `Die versicherte Person muss mindestens ${this.minAge} Jahre alt sein.`,
        ageTooHigh: this.formGroup?.get('isInsuree')?.value
            ? `Du darfst nicht älter als ${this.maxAge} Jahre sein, um eine Versicherung abschließen zu können.`
            : `Die versicherte Person darf nicht Älter als ${this.maxAge} Jahre sein.`,
        invalidDay: DATE_INVALID_DAY,
        invalidDateFormat: DATE_INVALID_DAY,
    }));

    public ngOnChanges(changes: SimpleChanges): void {
        // if available insurance ranks change check if the currently
        // selected rank is still vaild. if not set the highest possible
        // rank.
        const ranksChanges = changes['insuranceRanks'];
        if (
            ranksChanges &&
            Array.isArray(ranksChanges.previousValue) &&
            Array.isArray(ranksChanges.currentValue) &&
            ranksChanges.previousValue.length !== ranksChanges.currentValue.length &&
            this.formGroup.get('insuranceRankId').value
        ) {
            if (
                !changes['insuranceRanks'].currentValue.find(
                    (rank: InsuranceRank) => rank.id === this.formGroup.get('insuranceRankId').value,
                )
            ) {
                this.formGroup
                    .get('insuranceRankId')
                    .patchValue(
                        [...changes['insuranceRanks'].currentValue].sort(
                            (a: InsuranceRank, b: InsuranceRank) => b.id - a.id,
                        )[0].id,
                    );
            }
        }
    }
}
